import { Select, Stack, StackProps } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_LICENSE_EXPIRY_DURATION,
  LICENSE_EXPIRY_DURATIONS,
  LICENSE_EXPIRY_DURATION_LABEL_KEY,
  LicenseExpiryDuration,
  LicenseExpirySelection,
  isPastDay,
  parseDateOnlyOrNull,
} from "@utils/CommonUtil";

type LicenseExpirySelectProps = Omit<StackProps, "value" | "onChange"> & {
  value: LicenseExpirySelection;
  onChange: (value: LicenseExpirySelection) => void;
  description?: React.ReactNode;
  error?: React.ReactNode;
  disabled?: boolean;
  dropdownZIndex?: number;
};

export default function LicenseExpirySelect({
  value,
  onChange,
  description,
  error,
  disabled,
  dropdownZIndex,
  ...stackProps
}: LicenseExpirySelectProps) {
  const { t } = useTranslation();

  return (
    <Stack gap="xs" {...stackProps}>
      <Select
        label={t("licenseExpiry.label")}
        description={description}
        allowDeselect={false}
        disabled={disabled}
        data={LICENSE_EXPIRY_DURATIONS.map((duration) => ({
          value: duration,
          label: t(LICENSE_EXPIRY_DURATION_LABEL_KEY[duration]),
        }))}
        value={value.duration}
        onChange={(selected) =>
          onChange({
            ...value,
            duration: (selected as LicenseExpiryDuration | null) ?? DEFAULT_LICENSE_EXPIRY_DURATION,
          })
        }
        comboboxProps={dropdownZIndex ? { zIndex: dropdownZIndex } : undefined}
      />
      {value.duration === "custom" && (
        <DateInput
          label={t("licenseExpiry.customDate")}
          placeholder={t("licenseExpiry.customDatePlaceholder")}
          error={
            error ??
            (value.date && isPastDay(value.date) ? t("licenseExpiry.customDateInPast") : undefined)
          }
          minDate={new Date()}
          valueFormat="YYYY-MM-DD"
          clearable
          disabled={disabled}
          value={value.date}
          onChange={(selected) =>
            onChange({
              ...value,
              date: parseDateOnlyOrNull(selected),
              originalIso: undefined,
            })
          }
          popoverProps={dropdownZIndex ? { zIndex: dropdownZIndex } : undefined}
        />
      )}
    </Stack>
  );
}
